/**
 * components/ui/DropdownMenu.tsx
 *
 * Reusable dropdown menu component.
 * Uses React Portal to render the menu at document.body level,
 * preventing clipping by any ancestor overflow:hidden context.
 * Position is calculated dynamically from the trigger's bounding rect.
 * Updated 2026-03-11: replaced absolute positioning with Portal-based rendering.
 * Updated 2026-03-11: moved triggerRef from outer wrapper div to inner clickable div
 * so getBoundingClientRect() measures the actual trigger, not the full-width container.
 * Updated 2026-03-11: added align to useEffect dependency array so the menu repositions
 * correctly if the align prop changes while the menu is open.
 * Updated 2026-03-11: replaced useEffect with useLayoutEffect so calculatePosition runs
 * synchronously before paint, eliminating the first-frame flash at (0, 0).
 * Updated 2026-03-11: flip menu above trigger when available space below is insufficient.
 * Updated 2026-03-11: exported DropdownMenuItem, DropdownMenuSeparator, DropdownItem types
 * for use by consumers; outer wrapper made inline-block to avoid layout shifts.
 * Updated: replaced hardcoded zIndex 9999 with Z_INDEX.POPOVER from shared constants.
 */

import { useState, useRef, useLayoutEffect } from 'react';
import { Z_INDEX } from '../../constants/zIndex';
import { createPortal } from 'react-dom';

export interface DropdownMenuItem {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  variant?: 'default' | 'danger';
  disabled?: boolean;
}

export interface DropdownMenuSeparator {
  type: 'separator';
}

export type DropdownItem = DropdownMenuItem | DropdownMenuSeparator;

interface DropdownMenuProps {
  trigger: React.ReactNode;
  items: DropdownItem[];
  align?: 'left' | 'right';
}

interface MenuPosition {
  top: number;
  left?: number;
  right?: number;
}

export default function DropdownMenu({ trigger, items, align = 'right' }: DropdownMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<MenuPosition>({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const calculatePosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const menuHeight = menuRef.current?.offsetHeight ?? 0;
    const spaceBelow = window.innerHeight - rect.bottom;
    const top =
      menuHeight > 0 && spaceBelow < menuHeight + 8
        ? rect.top + window.scrollY - menuHeight - 8
        : rect.bottom + window.scrollY + 8;

    if (align === 'right') {
      setPosition({ top, right: window.innerWidth - rect.right - window.scrollX });
    } else {
      setPosition({ top, left: rect.left + window.scrollX });
    }
  };

  useLayoutEffect(() => {
    if (!isOpen) return;

    calculatePosition();

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        menuRef.current &&
        !menuRef.current.contains(target) &&
        triggerRef.current &&
        !triggerRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    const handleScrollOrResize = () => {
      calculatePosition();
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [isOpen, align]);

  const handleItemClick = (item: DropdownMenuItem) => {
    if (!item.disabled) {
      item.onClick();
      setIsOpen(false);
    }
  };

  const isSeparator = (item: DropdownItem): item is DropdownMenuSeparator => {
    return 'type' in item && item.type === 'separator';
  };

  const menuStyle: React.CSSProperties = {
    position: 'absolute',
    top: position.top,
    ...(position.right !== undefined ? { right: position.right } : { left: position.left }),
    width: '14rem',
    zIndex: Z_INDEX.POPOVER,
  };

  return (
    <div className="inline-block">
      <div ref={triggerRef} onClick={() => setIsOpen(!isOpen)} className="cursor-pointer">
        {trigger}
      </div>

      {isOpen && createPortal(
        <div
          ref={menuRef}
          style={menuStyle}
          className="rounded-lg bg-white shadow-soft-xl border border-neutral-200 py-1"
        >
          {items.map((item, index) => {
            if (isSeparator(item)) {
              return <hr key={index} className="border-neutral-200 my-1" />;
            }

            return (
              <button
                key={index}
                onClick={() => handleItemClick(item)}
                disabled={item.disabled}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors ${
                  item.disabled
                    ? 'opacity-50 cursor-not-allowed'
                    : item.variant === 'danger'
                    ? 'text-error-700 hover:bg-error-50'
                    : 'text-neutral-700 hover:bg-neutral-50'
                }`}
              >
                {item.icon && <span className="flex-shrink-0">{item.icon}</span>}
                <span className="flex-1">{item.label}</span>
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
}
